import React from 'react';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { useUserPlanStatus } from './useUserPlanStatus';
import { useUserPlanStatusWithTransactionLimit } from './useUserPlanStatusWithTransactionLimit';
import { AddonApiKey } from '../services/payment.types';
import { Box, Typography } from '@mui/material';

interface UseTransactionTooltipDataProps {
    allTimeTransactionCount: number;
    transactionCount: number;
    planLimit: number;
    selectedYear: string;
}

export const useTransactionTooltipData = ({
    allTimeTransactionCount,
    transactionCount,
    planLimit,
    selectedYear,
}: UseTransactionTooltipDataProps) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const { hasAddon, getPurchasedPackage } = useUserPlanStatusWithTransactionLimit();

    const rawPlanName = getPurchasedPackage(selectedYear) || 'standard';
    const planName = rawPlanName.charAt(0).toUpperCase() + rawPlanName.slice(1);
    const isPlanPurchased = !!getPurchasedPackage(selectedYear);
    const hasWeDoEverything = hasAddon(AddonApiKey.WE_DO_EVERYTHING);
    
    const effectiveTransactionCount = hasWeDoEverything ? allTimeTransactionCount : transactionCount;
    
    const transactionsLeft = planLimit > 0 ? planLimit - effectiveTransactionCount : (isPlanPurchased ? '∞' : '-');
    const formattedPlanLimit = planLimit > 0 ? planLimit.toString() : (isPlanPurchased ? '∞' : '-');
    
    // Calculate progress percentage, ensuring it's visible even with unlimited plan
    const progressPercentage = planLimit > 0 
        ? Math.min((effectiveTransactionCount / planLimit) * 100, 100) 
        : (isPlanPurchased ? 50 : 0); // If no limit and plan purchased, show 50%, else 0 if no plan

    const tooltipContent = (
        <Box sx={{ width: 290, p: 0, maxWidth: 290 }}>
            <Box sx={{ p: 1.5 }}>
                <Box sx={{ mb: 1, display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '0.9rem', flexGrow: 1 }}>
                        {hasWeDoEverything
                            ? (t('taxReports.allTimeTransactions'))
                            : `${t('taxReports.transactions')} ${selectedYear}:`
                        }
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '0.9rem', color: theme.palette.primary.main, ml: 2 }}>
                        {effectiveTransactionCount}
                    </Typography>
                </Box>
                
                <Box sx={{ mb: 1, display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '0.9rem', flexGrow: 1 }}>
                        {isPlanPurchased 
                            ? `${t('taxReports.transactionLimit')} (${t('plans.plan')}: ${planName})` 
                            : `${t('taxReports.transactionLimit')}:`}
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '0.9rem', ml: 2 }}>
                        {formattedPlanLimit}
                    </Typography>
                </Box>
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '0.9rem', flexGrow: 1 }}>
                        {t('taxReports.transactionsLeft')}:
                    </Typography>
                    <Typography 
                        variant="body2" 
                        sx={{ 
                            fontWeight: 'bold', 
                            fontSize: '0.9rem',
                            color: typeof transactionsLeft === 'number' && transactionsLeft < 20 ? 
                                theme.palette.error.main : theme.palette.success.main,
                            ml: 2
                        }}
                    >
                        {transactionsLeft}
                    </Typography>
                </Box>
            </Box>
        </Box>
    );

    return {
        t,
        theme, // Exposing theme in case components need it for other parts
        planName, // Though used in tooltip, might be useful
        isPlanPurchased, // Same as above
        transactionsLeft,
        formattedPlanLimit,
        tooltipContent,
        progressPercentage,
        effectiveTransactionCount,
    };
};
